import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { translatableKey } from '../../../../../utils';
import { Button, Accordion } from '@weezevent/nacre';

import LoadingCard from '../../../../../../../components/loadingCard';

import css from './actionlogs.module.css';

const ACTIONS = [
    { type: 'email_action', component: React.lazy(() => import('./EmailActionLog')) },
    { type: 'attendee_creation_action', component: React.lazy(() => import('./AttendeeCreationTransitionActionLog')) },
    { type: 'set_spam_score_action', component: React.lazy(() => import('./SetSpamScoreTransitionActionLog')) },
    { type: 'ban_organizer_action', component: React.lazy(() => import('./BanOrganizerTransitionActionLog')) }
];

class ActionLogBuilder {
    constructor(action) {
        this.action = action;
        this.actions = ACTIONS;
        this.component = this.actions.find(act => act.type === this.action);
        this.validActions = this.actions.map(act => act.type);
    }

    get_component() {
        return this.component?.component;
    }
}

const ActionLog = ({ log, datas, action, handleAction }) => {
    const { type } = action?.action;

    const ActionComponent = new ActionLogBuilder(type).get_component();

    if (!action || !ActionComponent) {
        return null;
    }

    const isLatestAction = React.useMemo(() => {
        const actions = datas?.reduce((acc, act) => {
            return acc.concat(act.action_logs.filter(ac => ac.action.type === type).map(ac => ac));
        }, []);

        if (!actions.length) {
            return false;
        }

        return actions[0].id === action.id;
    }, [datas, action, type]);

    const sortedActionDetails = React.useMemo(() => {
        return [...action.details].sort((a, b) => new Date(b.action_date) - new Date(a.action_date));
    }, [action.details]);

    return (
        <Suspense fallback={<LoadingCard loading />}>
            <ActionComponent log={log} action={action} handleAction={handleAction} isLatestAction={isLatestAction} sortedActionDetails={sortedActionDetails} />
        </Suspense>
    );
};

export const ActionLogWrapper = ({ action, subtitle = null, ctaButtons = [], children }) => {
    const { t } = useTranslation();

    const actionText = React.useMemo(() => {
        const text = action.text.trim().split(':');
        const { is_success } = action;

        const key = t(`ticket.moderation.attendee.history.action.${translatableKey(text[0])}`);
        const content = text[1] ?? '';
        const commentKey = text[2] && is_success ? t(`ticket.moderation.attendee.history.action.${translatableKey(text[2])}`) : '';
        const comment = text[3] ?? '';

        return `${key} ${content} ${commentKey} ${comment}`;
    }, [action]);

    return (
        <div className={css['log-action-block']}>
            <Accordion openByDefault title={`${t(`ticket.moderation.attendee.history.action.text`)}: ${action.action.name}`}>
                <span>
                    {actionText}
                    {subtitle}
                </span>
                {children}
                {ctaButtons.length > 0 && <Button.Group className={css['action-buttons']}>{ctaButtons}</Button.Group>}
            </Accordion>
        </div>
    );
};

export const SpamLogEntry = ({ historyData }) => {
    const { t } = useTranslation();
    return (
        <>
            <div>
                <span>{`${t('ticket.moderation.attendee.history.score')}: `}</span>
                <b>{historyData.score}</b>
            </div>
            {!!historyData.reasons?.length && (
                <div>
                    <span>{`${t('ticket.moderation.attendee.history.reasons')}: `}</span>
                    <b>{historyData.reasons?.join('; ')}</b>
                </div>
            )}
        </>
    );
};

export const ActionLogsList = ({ historyData, datas, handleAction, attendee }) => {
    const { t } = useTranslation();

    const historyText = React.useMemo(() => {
        return historyData.text
            .replaceAll('True', 'checked')
            .replaceAll('False', 'not checked')
            .split('\n');
    }, [historyData]);

    const formatLogText = value => {
        // Differentiate action messages from basic transition statuses
        if (!value.includes('->')) {
            return t(`ticket.moderation.attendee.history.action.${translatableKey(value)}`);
        }
        return (
            <span>
                <b>{`${t('ticket.moderation.attendee.history.transition')}: `}</b>
                {value}
            </span>
        );
    };

    const formatLogValue = React.useCallback(
        value => {
            // Ensure a clear pattern and match on form update action logs
            const action = historyText[0].toLowerCase().replaceAll(' ', '_');

            if (!action.includes('form_updated')) return value;

            const formKey = value.split(':')[0];
            const formLabel = attendee.form.find(it => it.weez_id === formKey || it.source === formKey)?.question;
            return formKey && formLabel ? value.replace(formKey, formLabel) : value;
        },
        [historyText, attendee.form]
    );
    return (
        <>
            <div className={css['attendee-history-actionName']}>
                {historyText.map((it, index) => {
                    if (index === 0) {
                        return <p key={index}>{formatLogText(it)}</p>;
                    }
                    return <li key={index}>{formatLogValue(it)}</li>;
                })}
            </div>
            <div className={css['attendee-history-actionName']}>
                {historyData.action_logs
                    .sort((a, b) => b.created?.localeCompare(a?.created))
                    .map(action => (
                        <ActionLog key={action.id} log={historyData} datas={datas} action={action} handleAction={handleAction} />
                    ))}
            </div>
        </>
    );
};

export default ActionLog;
